import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, LogOut, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/fleet", label: "Fleet" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar({ marketing = false }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth() || {};
  const loc = useLocation();
  const nav = useNavigate();

  const activeCls = "text-[#D4AF37]";
  const linkCls = marketing ? "text-white/80 hover:text-white" : "text-[#0B1F3A]/80 hover:text-[#0B1F3A]";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 ${marketing ? "glass-nav" : "bg-white/90 backdrop-blur-md border-b border-black/5"}`}>
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={2} />
          <div className="leading-tight">
            <div className={`font-display font-extrabold text-lg tracking-tight ${marketing ? "text-white" : "text-[#0B1F3A]"}`}>Global Prime Fleet</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Drive Excellence</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors ${loc.pathname === l.to ? activeCls : linkCls}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="btn-ghost !py-2 !px-4 !text-sm">Admin</Link>
              )}
              <Link to="/my-bookings" data-testid="nav-my-bookings" className={`text-sm flex items-center gap-2 ${linkCls}`}>
                <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className={`text-sm ${linkCls} flex items-center gap-1`}>
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className={`text-sm ${linkCls}`}>Sign in</Link>
              <Link to="/fleet" data-testid="nav-book-now" className="btn-gold !py-2 !px-5 !text-sm">Book Now</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-current" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className={`lg:hidden ${marketing ? "bg-[#040B16]" : "bg-white"} border-t ${marketing ? "border-white/10" : "border-black/5"}`}>
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={`text-sm ${linkCls}`}>{l.label}</Link>
            ))}
            <div className="hairline my-2" />
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className={linkCls}>Admin</Link>}
                <Link to="/my-bookings" onClick={() => setOpen(false)} className={linkCls}>My Bookings</Link>
                <button onClick={() => { logout(); setOpen(false); nav("/"); }} className={`text-left ${linkCls}`}>Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className={linkCls}>Sign in</Link>
                <Link to="/fleet" onClick={() => setOpen(false)} className="btn-gold !py-2 !px-5 !text-sm w-fit">Book Now</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
